import './TimeSlotSelector.scss';

import * as React from 'react';

import { css } from '../../../util/Css';
import { TimeSlotStatus } from '../../../api/timeslots.types';
import { DefaultButton } from '@fluentui/react';

export interface TimeSlotOption {
  id: string;
  title: string;
  available: boolean;
  isDisabled: boolean;
  slotStatus: TimeSlotStatus;
  proximal?: boolean;
}

export interface TimeSlotSelectorProps {
  timeslots: TimeSlotOption[];
  selectedId: string;
  onSelect: (id: string, title: string) => void;
}

export interface TimeSlotSelectorState {}

export class TimeSlotSelector extends React.Component<TimeSlotSelectorProps, TimeSlotSelectorState> {
  public render(): JSX.Element {
    return <div className="slot-grid">{this.renderRows()}</div>;
  }

  private renderRows(): JSX.Element[] {
    const rows: JSX.Element[] = [];
    const maxItemsPerRow = 2;

    for (let i = 0, len = this.props.timeslots.length; i < len; i++) {
      if (i % maxItemsPerRow === 0) {
        const cells: JSX.Element[] = [];
        const jlen = Math.min(i + maxItemsPerRow, this.props.timeslots.length);

        for (let j = i; j < jlen; j++) {
          const timeslotOption = this.props.timeslots[j];
          const onClick = () => {
            this.props.onSelect(timeslotOption.id, timeslotOption.title);
          };

          const isDelayedSlot = timeslotOption.slotStatus === 'LOCATION_DELAYED_SERVICE';

          const visualStates = {
            proximal: !!timeslotOption.proximal,
            full: !timeslotOption.available && !timeslotOption.isDisabled,
            delayed: isDelayedSlot,
            selected: timeslotOption.id === this.props.selectedId,
          };

          const classNames = css('slot', visualStates);

          const automationId = timeslotOption.available ? `slot_${timeslotOption.id}_available` : `slot_${timeslotOption.id}`;

          /**
           * The previous implementation of this component used a table of <td>s with
           * onClicks, which was extremely inaccessible. Because I was making changes in
           * here already, I'm adding an aria-description for screen readers which
           * announces the slot's visual state. Previously, states like this were only
           * indicated via styling. This is still the case in many other places.
           * - JG 9/2022
           */
          const visualDescriptions = {
            proximal: 'recommended',
            full: 'full',
            delayed: 'delayed',
            selected: 'selected',
          };

          let description = !timeslotOption.isDisabled
            ? Object.entries(visualStates)
                .filter(([_, value]) => !!value)
                .map(([key, _]) => visualDescriptions[key as keyof typeof visualStates])
                .join(', ')
            : '';

          cells.push(
            <DefaultButton
              data-tid={automationId}
              key={`${i}_${j}`}
              className={classNames}
              toggle
              text={timeslotOption.title}
              iconProps={timeslotOption.proximal ? { iconName: 'FavoriteStarFill', style: { color: '#0078d4' } } : { iconName: 'undefined' }}
              onClick={timeslotOption.isDisabled ? undefined : onClick}
              role="radio"
              ariaDescription={description}
              checked={timeslotOption.id === this.props.selectedId}
              disabled={timeslotOption.isDisabled}
            />
          );
        }

        rows.push(
          <div className="slot-row" role="radiogroup" key={i}>
            {cells}
          </div>
        );
      }
    }
    return rows;
  }
}
